/**
 * Envía una respuesta humana al usuario externo (DM o comentario).
 *
 * Si la cuenta está en dry-run, persiste pero no llega a la red. Si está en
 * kill-switch, el backend devuelve 423. Si el mensaje ya fue respondido, 409.
 *
 * `internalNote: true` guarda como nota interna sin enviar nada externamente.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiPost } from "@/lib/api";

interface SendMessagePayload {
  conversationId: string;
  messageId: number;
  body: string;
  internalNote?: boolean;
}

interface SendResponse {
  ok: boolean;
  sent: boolean;
  kind: "reply" | "internal_note";
  dryRun?: boolean;
}

export function useSendMessage() {
  const qc = useQueryClient();

  return useMutation<SendResponse, Error, SendMessagePayload>({
    mutationFn: async ({ conversationId, messageId, body, internalNote }) =>
      apiPost<SendResponse>(`/conversations/${conversationId}/messages`, {
        messageId,
        body,
        internalNote: internalNote ?? false,
      }),
    onSuccess: (data) => {
      // El detalle del thread está en ["conversation-detail", id]; las claves
      // viejas no matcheaban y obligaban a recargar para ver el mensaje nuevo.
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["conversation-detail"] });

      if (data.kind === "internal_note") {
        toast.success("Nota interna guardada");
      } else if (data.dryRun) {
        toast.success("Respuesta guardada (modo prueba — no se envió a la red)");
      } else {
        toast.success("Respuesta enviada");
      }
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });
}
